import { useCallback, useEffect, useState } from "react";

/**
 * useCookieNotice — manages the user's acknowledgement of our cookie notice.
 *
 * Why this is just an "ack" and not a full consent manager:
 *   AutoAds currently uses ONLY essential cookies (session, CSRF, auth).
 *   Under Polish law (art. 173 Prawa telekomunikacyjnego) and ePrivacy,
 *   strictly necessary cookies do NOT require opt-in consent — we only
 *   need to inform the user. Sentry in our config sets no cookies.
 *
 *   When/if we add analytics or marketing cookies, this hook should be
 *   replaced (or extended) with a real consent manager that gates those
 *   trackers behind explicit opt-in.
 *
 * Storage:
 *   - localStorage key `cookie_notice_ack_v1` = JSON { ackedAt, version }
 *   - 12-month retention (we re-prompt after that)
 *
 * Returns:
 *   { isAcked: bool, isLoaded: bool, ack: () => void, reset: () => void }
 */

const STORAGE_KEY = "cookie_notice_ack_v1";
const NOTICE_VERSION = "1.1"; // bump when the cookie policy materially changes
const TTL_DAYS = 365;

const isExpired = (ackedAt) => {
    if (!ackedAt) return true;
    const ackedMs = new Date(ackedAt).getTime();
    if (Number.isNaN(ackedMs)) return true;
    const ageMs = Date.now() - ackedMs;
    return ageMs > TTL_DAYS * 24 * 60 * 60 * 1000;
};

export const useCookieNotice = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isAcked, setIsAcked] = useState(false);

    useEffect(() => {
        // Defer to next tick to avoid hydration mismatch and to not block paint.
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                const sameVersion = parsed?.version === NOTICE_VERSION;
                const stillFresh = !isExpired(parsed?.ackedAt);
                setIsAcked(sameVersion && stillFresh);
            }
        } catch (e) {
            // localStorage unavailable (private mode / disabled) — show notice every visit, that's fine.
            // eslint-disable-next-line no-console
            console.warn("[cookie-notice] storage read failed:", e?.message);
        }
        setIsLoaded(true);
    }, []);

    const ack = useCallback(() => {
        const payload = {
            ackedAt: new Date().toISOString(),
            version: NOTICE_VERSION,
        };
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (e) {
            // Storage blocked — still hide the banner for this session.
            // eslint-disable-next-line no-console
            console.warn("[cookie-notice] storage write failed:", e?.message);
        }
        setIsAcked(true);
    }, []);

    const reset = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            // ignore
        }
        setIsAcked(false);
    }, []);

    return { isLoaded, isAcked, ack, reset };
};

export default useCookieNotice;
